import React, { useState, useCallback } from 'react';
import './CustomTasksPage.css';
import { PageHeader } from '../../components/PageHeader/PageHeader';
import { BottomSheet } from '../../components/BottomSheet/BottomSheet';
import { EmptyState } from '../../components/EmptyState/EmptyState';
import type { CustomTask } from '../../types';
import { CATEGORY_COLORS, PRIORITY_COLORS } from '../../types';

interface CustomTasksPageProps {
  tasks: CustomTask[];
  onAdd: (task: Omit<CustomTask, 'id' | 'completed' | 'createdAt'>) => void;
  onToggle: (id: string) => void;
  onDelete: (id: string) => void;
  showToast: (msg: string) => void;
}

const CATEGORIES = ['General', 'Errands', 'Shopping', 'Home Repair', 'Cleaning', 'Outdoor', 'Other'];
type FilterMode = 'all' | 'pending' | 'completed';
type SortMode = 'newest' | 'priority' | 'category';

export const CustomTasksPage: React.FC<CustomTasksPageProps> = ({
  tasks, onAdd, onToggle, onDelete, showToast,
}) => {
  const [showAdd, setShowAdd] = useState(false);
  const [filter, setFilter] = useState<FilterMode>('all');
  const [sort, setSort] = useState<SortMode>('newest');

  // Add form state
  const [label, setLabel] = useState('');
  const [category, setCategory] = useState('General');
  const [priority, setPriority] = useState<'low' | 'medium' | 'high'>('medium');
  const [dueDate, setDueDate] = useState('');

  const handleAdd = () => {
    if (!label.trim()) return;
    onAdd({ label: label.trim(), category, priority, dueDate: dueDate || undefined });
    setLabel('');
    setCategory('General');
    setPriority('medium');
    setDueDate('');
    setShowAdd(false);
    showToast('Task added');
  };

  const handleDelete = useCallback((id: string) => {
    onDelete(id);
    showToast('Task deleted');
  }, [onDelete, showToast]);

  // Filter and sort
  let filtered = [...tasks];
  if (filter === 'pending') filtered = filtered.filter((t) => !t.completed);
  if (filter === 'completed') filtered = filtered.filter((t) => t.completed);

  const priorityOrder = { high: 0, medium: 1, low: 2 };
  if (sort === 'newest') filtered.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  if (sort === 'priority') filtered.sort((a, b) => priorityOrder[a.priority] - priorityOrder[b.priority]);
  if (sort === 'category') filtered.sort((a, b) => a.category.localeCompare(b.category));

  const pendingCount = tasks.filter((t) => !t.completed).length;

  return (
    <div className="custom-tasks-page">
      <PageHeader
        title="My Tasks"
        rightAction={
          <button className="ct-add-btn" onClick={() => setShowAdd(true)} aria-label="Add task">
            <svg viewBox="0 0 24 24" width="22" height="22">
              <line x1="12" y1="5" x2="12" y2="19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              <line x1="5" y1="12" x2="19" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        }
      />

      <div className="ct-content">
        {/* Filters */}
        <div className="ct-controls">
          <div className="ct-filters">
            {(['all', 'pending', 'completed'] as FilterMode[]).map((f) => (
              <button
                key={f}
                className={`ct-filter-chip ${filter === f ? 'ct-filter-chip--active' : ''}`}
                onClick={() => setFilter(f)}
              >
                {f === 'all' ? `All (${tasks.length})` : f === 'pending' ? `Pending (${pendingCount})` : `Done (${tasks.length - pendingCount})`}
              </button>
            ))}
          </div>
          <select className="ct-sort" value={sort} onChange={(e) => setSort(e.target.value as SortMode)}>
            <option value="newest">Newest</option>
            <option value="priority">Priority</option>
            <option value="category">Category</option>
          </select>
        </div>

        {/* Task list */}
        {filtered.length === 0 ? (
          <EmptyState
            emoji="📝"
            title={filter === 'all' ? 'No tasks yet' : `No ${filter} tasks`}
            description={filter === 'all' ? 'Tap + to add your first custom task' : 'Try changing the filter'}
            actionLabel={filter === 'all' ? 'Add Task' : undefined}
            onAction={filter === 'all' ? () => setShowAdd(true) : undefined}
          />
        ) : (
          <div className="ct-list">
            {filtered.map((task) => (
              <div key={task.id} className={`ct-task ${task.completed ? 'ct-task--done' : ''}`}>
                <button className="ct-task-check" onClick={() => onToggle(task.id)}
                  style={{ '--ct-color': PRIORITY_COLORS[task.priority] } as React.CSSProperties}
                >
                  {task.completed ? (
                    <svg viewBox="0 0 16 16" width="16" height="16">
                      <polyline points="3.5 8 6.5 11 12.5 5" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  ) : null}
                </button>
                <div className="ct-task-body">
                  <span className="ct-task-label">{task.label}</span>
                  <div className="ct-task-meta">
                    <span className="ct-task-category" style={{ color: CATEGORY_COLORS[task.category] || '#888' }}>
                      {task.category}
                    </span>
                    {task.dueDate && <span className="ct-task-due">Due: {task.dueDate}</span>}
                  </div>
                </div>
                <button className="ct-task-delete" onClick={() => handleDelete(task.id)} aria-label="Delete">
                  <svg viewBox="0 0 20 20" width="16" height="16">
                    <line x1="5" y1="5" x2="15" y2="15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    <line x1="15" y1="5" x2="5" y2="15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add task sheet */}
      <BottomSheet open={showAdd} onClose={() => setShowAdd(false)} title="New Task">
        <div className="ct-form">
          <input
            className="ct-input"
            placeholder="What needs to be done?"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            autoFocus
          />

          <label className="ct-form-label">Category</label>
          <div className="ct-category-chips">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                className={`ct-cat-chip ${category === c ? 'ct-cat-chip--active' : ''}`}
                onClick={() => setCategory(c)}
                style={{ '--chip-color': CATEGORY_COLORS[c] } as React.CSSProperties}
              >
                {c}
              </button>
            ))}
          </div>

          <label className="ct-form-label">Priority</label>
          <div className="ct-priority-chips">
            {(['low', 'medium', 'high'] as const).map((p) => (
              <button
                key={p}
                className={`ct-pri-chip ${priority === p ? 'ct-pri-chip--active' : ''}`}
                onClick={() => setPriority(p)}
                style={{ '--pri-color': PRIORITY_COLORS[p] } as React.CSSProperties}
              >
                {p.charAt(0).toUpperCase() + p.slice(1)}
              </button>
            ))}
          </div>

          <label className="ct-form-label">Due Date (optional)</label>
          <input
            className="ct-input"
            type="date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
          />

          <button className="ct-submit" onClick={handleAdd} disabled={!label.trim()}>
            Add Task
          </button>
        </div>
      </BottomSheet>
    </div>
  );
};
